import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, Chip } from '@mui/material';
import { Business, Person, Star, TrendingUp } from '@mui/icons-material';

interface HorizontalBarDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

const categoryNames: Record<string, { hebrew: string; english: string }> = {
  'management': { hebrew: 'ניהול', english: 'Management' },
  'organization': { hebrew: 'ארגון', english: 'Organization' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
  'finance': { hebrew: 'כספים', english: 'Finance' },
  'operations': { hebrew: 'תפעול', english: 'Operations' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
  'stress': { hebrew: 'מתח', english: 'Stress' },
  'principles': { hebrew: 'עקרונות', english: 'Principles' },
  'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help' },
};

const HorizontalBarDashboard: React.FC<HorizontalBarDashboardProps> = ({ categoryScores, language }) => {
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),
      personal: scores.slice(7, 10),
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
    };
  }, [categoryScores, language]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#2ecc71';
    if (score >= 40) return '#f39c12';
    return '#e74c3c';
  };

  const getBenchmarkLabel = (score: number) => {
    if (language === 'he') {
      if (score >= 80) return 'מעולה';
      if (score >= 60) return 'טוב מאוד';
      if (score >= 40) return 'סביר';
      return 'דורש שיפור';
    } else {
      if (score >= 80) return 'Excellent';
      if (score >= 60) return 'Very Good';
      if (score >= 40) return 'Fair';
      return 'Needs Improvement';
    }
  };

  const renderBar = (category: any, index: number) => (
    <Box key={category.key} sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" fontWeight="600" sx={{ minWidth: '180px' }}>
            {index + 1}. {category.name}
          </Typography>
          {category.value >= 70 && <Star sx={{ fontSize: 18, color: '#f39c12' }} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            label={getBenchmarkLabel(category.value)}
            size="small"
            sx={{
              backgroundColor: getScoreColor(category.value),
              color: 'white',
              fontWeight: 'bold',
              minWidth: '100px'
            }}
          />
          <Typography variant="h6" fontWeight="bold" sx={{ color: getScoreColor(category.value), minWidth: '60px', textAlign: 'right' }}>
            {category.value.toFixed(0)}%
          </Typography>
        </Box>
      </Box>
      <Box sx={{ position: 'relative', height: '32px', backgroundColor: '#f5f5f5', borderRadius: '4px', overflow: 'hidden' }}>
        {/* Benchmark zones */}
        <Box sx={{ position: 'absolute', left: '0%', width: '40%', height: '100%', backgroundColor: 'rgba(231, 76, 60, 0.1)' }} />
        <Box sx={{ position: 'absolute', left: '40%', width: '20%', height: '100%', backgroundColor: 'rgba(243, 156, 18, 0.1)' }} />
        <Box sx={{ position: 'absolute', left: '60%', width: '20%', height: '100%', backgroundColor: 'rgba(46, 204, 113, 0.1)' }} />
        <Box sx={{ position: 'absolute', left: '80%', width: '20%', height: '100%', backgroundColor: 'rgba(39, 174, 96, 0.1)' }} />

        {/* Benchmark lines */}
        {[40, 60, 80].map(mark => (
          <Box
            key={mark}
            sx={{
              position: 'absolute',
              left: `${mark}%`,
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: '#999',
              opacity: 0.4
            }}
          />
        ))}

        {/* Progress bar with gradient */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${category.value}%`,
            background: `linear-gradient(90deg, ${getScoreColor(category.value)}dd, ${getScoreColor(category.value)})`,
            borderRadius: '4px',
            transition: 'width 0.6s ease-in-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: '8px'
          }}
        >
          <Typography variant="caption" fontWeight="bold" sx={{ color: 'white' }}>
            {category.value.toFixed(0)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
        {language === 'he' ? '📊 ניתוח השוואתי עם בנצ\'מארק' : '📊 Comparative Benchmark Analysis'}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business /> {language === 'he' ? 'ביצועים עסקיים' : 'Business Performance'}
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" sx={{ mt: 1 }}>
                    {processedScores.businessAverage.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    {language === 'he' ? 'ממוצע 7 קטגוריות' : 'Average of 7 categories'}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 64, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person /> {language === 'he' ? 'ביצועים אישיים' : 'Personal Performance'}
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" sx={{ mt: 1 }}>
                    {processedScores.personalAverage.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    {language === 'he' ? 'ממוצע 3 קטגוריות' : 'Average of 3 categories'}
                  </Typography>
                </Box>
                <Star sx={{ fontSize: 64, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Business Categories */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, pb: 1, borderBottom: '3px solid #667eea' }}>
          🏢 {language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}
        </Typography>
        {processedScores.business.map((cat, idx) => renderBar(cat, idx))}
      </Paper>

      {/* Personal Categories */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, pb: 1, borderBottom: '3px solid #f5576c' }}>
          👤 {language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}
        </Typography>
        {processedScores.personal.map((cat, idx) => renderBar(cat, idx))}
      </Paper>

      {/* Legend */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, backgroundColor: '#e74c3c', borderRadius: 1 }} />
          <Typography variant="caption">0-40%: {language === 'he' ? 'דורש שיפור' : 'Needs Improvement'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, backgroundColor: '#f39c12', borderRadius: 1 }} />
          <Typography variant="caption">40-60%: {language === 'he' ? 'סביר' : 'Fair'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, backgroundColor: '#2ecc71', borderRadius: 1 }} />
          <Typography variant="caption">60-80%: {language === 'he' ? 'טוב מאוד' : 'Very Good'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, backgroundColor: '#27ae60', borderRadius: 1 }} />
          <Typography variant="caption">80-100%: {language === 'he' ? 'מעולה' : 'Excellent'}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HorizontalBarDashboard;
